// helpers/element-builder.js
var El = class _El {
  constructor(tagname) {
    this.element = document.createElement(tagname);
  }
  id(s) {
    this.element.id = s;
    return this;
  }
  class(s) {
    this.element.classList.add(s);
    return this;
  }
  attrs(obj) {
    for (const [k, v] of Object.entries(obj)) {
      this.element.setAttribute(k, v);
    }
    return this;
  }
  text(t) {
    this.element.innerText = t;
    return this;
  }
  html(t) {
    this.element.innerHTML = t;
    return this;
  }
  handle(e, f) {
    this.element.addEventListener(e, f);
    return this;
  }
  addTo(el) {
    if (el instanceof _El) {
      el.element.appendChild(this.element);
    } else {
      el.appendChild(this.element);
    }
    return this.element;
  }
};

// components/input.js
var asyncSleep = async (ms = 100) => {
  return new Promise((r) => setTimeout(r, ms));
};
var Input = class {
  constructor(opts = {}) {
    this.inputEl = null;
    this.clearEl = null;
    this.instance = null;
    this.searchID = 0;
    this.debounceTimeoutMs = opts.debounceTimeoutMs ?? 300;
    if (opts.inputElement) {
      if (opts.containerElement) {
        console.warn(`[Pagefind Input component]: inputElement and containerElement both supplied. Ignoring the container option.`);
        return;
      }
      this.initExisting(opts.inputElement);
    } else if (opts.containerElement) {
      this.initContainer(opts.containerElement);
    } else {
      console.error(`[Pagefind Input component]: No selector supplied for containerElement or inputElement`);
      return;
    }
    this.inputEl.addEventListener("input", async (e) => {
      if (this.instance && typeof e?.target?.value === "string") {
        this.updateState(e.target.value);
        const thisSearchID = ++this.searchID;
        await asyncSleep(this.debounceTimeoutMs);
        if (thisSearchID !== this.searchID) {
          return null;
        }
        this.instance?.triggerSearch(e.target.value);
      }
    });
    this.inputEl.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        ++this.searchID;
        this.inputEl.value = "";
        this.instance?.triggerSearch("");
        this.updateState("");
      }
      if (e.key === "Enter") {
        e.preventDefault();
      }
    });
    this.inputEl.addEventListener("focus", () => {
      this.instance?.triggerLoad();
    });
  }
  initContainer(selector) {
    const container = document.querySelector(selector);
    if (!container) {
      console.error(`[Pagefind Input component]: No container found for ${selector} selector`);
      return;
    }
    if (container.tagName === "INPUT") {
      console.warn(`[Pagefind Input component]: Encountered input element for ${selector} when a container was expected`);
      console.warn(`[Pagefind Input component]: Treating containerElement option as inputElement and proceeding`);
      this.initExisting(selector);
    } else {
      container.innerHTML = "";
      let id = 0;
      while (document.querySelector(`#pfmod-input-${id}`)) {
        id += 1;
      }
      const wrapper = new El("form").class("pagefind-modular-input-wrapper").attrs({
        role: "search",
        "aria-label": "Search this site",
        action: "javascript:void(0);"
      });
      new El("label").attrs({
        "for": `pfmod-input-${id}`,
        "data-pfmod-sr-hidden": "true"
      }).text("Search this site").addTo(wrapper);
      this.inputEl = new El("input").id(`pfmod-input-${id}`).class("pagefind-modular-input").attrs({
        autocapitalize: "none",
        enterkeyhint: "search"
      }).addTo(wrapper);
      this.clearEl = new El("button").class("pagefind-modular-input-clear").attrs({ "data-pfmod-suppressed": "true" }).text("Clear").handle("click", () => {
        this.inputEl.value = "";
        this.instance.triggerSearch("");
        this.updateState("");
      }).addTo(wrapper);
      wrapper.addTo(container);
    }
  }
  initExisting(selector) {
    const el = document.querySelector(selector);
    if (!el) {
      console.error(`[Pagefind Input component]: No input element found for ${selector} selector`);
      return;
    }
    if (el.tagName !== "INPUT") {
      console.error(`[Pagefind Input component]: Expected ${selector} to be an <input> element`);
      return;
    }
    this.inputEl = el;
  }
  updateState(term) {
    if (this.clearEl) {
      if (term && term?.length) {
        this.clearEl.removeAttribute("data-pfmod-suppressed");
      } else {
        this.clearEl.setAttribute("data-pfmod-suppressed", "true");
      }
    }
  }
  register(instance) {
    this.instance = instance;
    this.instance.on("search", (term, _filters) => {
      if (this.inputEl && document.activeElement !== this.inputEl) {
        this.inputEl.value = term;
        this.updateState(term);
      }
    });
  }
  focus() {
    if (this.inputEl) {
      this.inputEl.focus();
    }
  }
};

// components/resultList.js
var templateNodes = (templateResult) => {
  if (templateResult instanceof Element) {
    return [templateResult];
  } else if (Array.isArray(templateResult) && templateResult.every((r) => r instanceof Element)) {
    return templateResult;
  } else if (typeof templateResult === "string" || templateResult instanceof String) {
    let wrap = document.createElement("div");
    wrap.innerHTML = templateResult;
    return [...wrap.childNodes];
  } else {
    console.error(`[Pagefind ResultList component]: Expected template function to return an HTML element or string, got ${typeof templateResult}`);
    return [];
  }
};
var placeholderTemplate = () => {
  const placeholder = (max = 30) => {
    return ". ".repeat(Math.floor(10 + Math.random() * max));
  };
  return `<li class="pagefind-modular-list-result">
    <div class="pagefind-modular-list-thumb" data-pfmod-loading></div>
    <div class="pagefind-modular-list-inner">
        <p class="pagefind-modular-list-title" data-pfmod-loading>${placeholder(30)}</p>
        <p class="pagefind-modular-list-excerpt" data-pfmod-loading>${placeholder(40)}</p>
    </div>
</li>`;
};
var resultTemplate = (result) => {
  let wrapper = new El("li").class("pagefind-modular-list-result");
  let thumb = new El("div").class("pagefind-modular-list-thumb").addTo(wrapper);
  if (result?.meta?.image) {
    new El("img").class("pagefind-modular-list-image").attrs({
      src: result.meta.image,
      alt: result.meta.image_alt || result.meta.title
    }).addTo(thumb);
  }
  let inner = new El("div").class("pagefind-modular-list-inner").addTo(wrapper);
  let title = new El("p").class("pagefind-modular-list-title").addTo(inner);
  new El("a").class("pagefind-modular-list-link").text(result.meta?.title).attrs({
    href: result.meta?.url || result.url
  }).addTo(title);
  new El("p").class("pagefind-modular-list-excerpt").html(result.excerpt).addTo(inner);
  return wrapper.element;
};
var nearestScrollParent = (el) => {
  if (!(el instanceof HTMLElement))
    return null;
  const overflowY = window.getComputedStyle(el).overflowY;
  const isScrollable = overflowY !== "visible" && overflowY !== "hidden";
  if (isScrollable) {
    return el;
  } else {
    return nearestScrollParent(el.parentNode);
  }
};
var Result = class {
  constructor(opts = {}) {
    this.rawResult = opts.result;
    this.placeholderNodes = opts.placeholderNodes;
    this.resultFn = opts.resultFn;
    this.intersectionEl = opts.intersectionEl;
    this.result = null;
    this.waitForIntersection();
  }
  waitForIntersection() {
    if (!this.placeholderNodes?.length)
      return;
    let options = {
      root: this.intersectionEl,
      rootMargin: "0px",
      threshold: 0.01
    };
    let observer = new IntersectionObserver((entries, observer2) => {
      if (this.result !== null)
        return;
      if (entries?.[0]?.isIntersecting) {
        this.load();
        observer2.disconnect();
      }
    }, options);
    observer.observe(this.placeholderNodes[0]);
  }
  async load() {
    if (!this.placeholderNodes?.length)
      return;
    this.result = await this.rawResult.data();
    const resultTemplate2 = this.resultFn(this.result);
    const resultNodes = templateNodes(resultTemplate2);
    while (this.placeholderNodes.length > 1) {
      this.placeholderNodes.pop().remove();
    }
    this.placeholderNodes[0].replaceWith(...resultNodes);
  }
};
var ResultList = class {
  constructor(opts) {
    this.intersectionEl = document.body;
    this.containerEl = null;
    this.results = [];
    this.placeholderTemplate = opts.placeholderTemplate ?? placeholderTemplate;
    this.resultTemplate = opts.resultTemplate ?? resultTemplate;
    if (opts.containerElement) {
      this.initContainer(opts.containerElement);
    } else {
      console.error(`[Pagefind ResultList component]: No selector supplied for containerElement`);
      return;
    }
  }
  initContainer(selector) {
    const container = document.querySelector(selector);
    if (!container) {
      console.error(`[Pagefind ResultList component]: No container found for ${selector} selector`);
      return;
    }
    this.containerEl = container;
  }
  append(nodes) {
    for (const node of nodes) {
      this.containerEl.appendChild(node);
    }
  }
  register(instance) {
    instance.on("results", (results) => {
      if (!this.containerEl)
        return;
      this.containerEl.innerHTML = "";
      this.intersectionEl = nearestScrollParent(this.containerEl);
      this.results = results.results.map((r) => {
        let placeholderNodes = templateNodes(this.placeholderTemplate());
        this.append(placeholderNodes);
        return new Result({ result: r, placeholderNodes, resultFn: this.resultTemplate, intersectionEl: this.intersectionEl });
      });
    });
    instance.on("loading", () => {
      if (!this.containerEl)
        return;
      this.containerEl.innerHTML = "";
    });
  }
};

// components/summary.js
var Summary = class {
  constructor(opts = {}) {
    this.containerEl = null;
    this.defaultMessage = opts.defaultMessage ?? "";
    this.term = "";
    if (opts.containerElement) {
      this.initContainer(opts.containerElement);
    } else {
      console.error(`[Pagefind Summary component]: No selector supplied for containerElement`);
      return;
    }
  }
  initContainer(selector) {
    const container = document.querySelector(selector);
    if (!container) {
      console.error(`[Pagefind Summary component]: No container found for ${selector} selector`);
      return;
    }
    this.containerEl = container;
    this.containerEl.innerText = this.defaultMessage;
  }
  register(instance) {
    instance.on("search", (term, _filters) => {
      this.term = term;
    });
    instance.on("results", (results) => {
      if (!this.containerEl || !results)
        return;
      if (!this.term) {
        this.containerEl.innerText = this.defaultMessage;
        return;
      }
      let count = results?.results?.length ?? 0;
      this.containerEl.innerText = `${count} result${count === 1 ? "" : "s"} for ${this.term}`;
    });
    instance.on("loading", () => {
      if (!this.containerEl)
        return;
      this.containerEl.innerText = `Searching for ${this.term}...`;
    });
  }
};

// components/filterPills.js
var FilterPills = class {
  constructor(opts = {}) {
    this.instance = null;
    this.wrapper = null;
    this.pillContainer = null;
    this.available = {};
    this.selected = ["All"];
    this.total = 0;
    this.filterMemo = "";
    this.filter = opts.filter;
    this.ordering = opts.ordering ?? null;
    this.alwaysShow = opts.alwaysShow ?? false;
    this.selectMultiple = opts.selectMultiple ?? false;
    if (!this.filter?.length) {
      console.error(`[Pagefind FilterPills component]: No filter option supplied, nothing to display`);
      return;
    }
    if (opts.containerElement) {
      this.initContainer(opts.containerElement);
    } else {
      console.error(`[Pagefind FilterPills component]: No selector supplied for containerElement`);
      return;
    }
  }
  initContainer(selector) {
    const container = document.querySelector(selector);
    if (!container) {
      console.error(`[Pagefind FilterPills component]: No container found for ${selector} selector`);
      return;
    }
    container.innerHTML = "";
    const id = `pagefind_modular_filter_pills_${this.filter}`;
    const wrapper = new El("div").class("pagefind-modular-filter-pills-wrapper").attrs({
      "role": "group",
      "aria-labelledby": id
    });
    if (!this.alwaysShow) {
      wrapper.attrs({ "data-pfmod-hidden": true });
    }
    new El("div").id(id).class("pagefind-modular-filter-pills-label").attrs({
      "data-pfmod-sr-hidden": true
    }).text(`Filter results by ${this.filter}`).addTo(wrapper);
    this.pillContainer = new El("div").class("pagefind-modular-filter-pills").addTo(wrapper);
    this.wrapper = wrapper.addTo(container);
  }
  update() {
    const filterMemo = this.available.map((a) => a[0]).join("~");
    if (filterMemo == this.filterMemo) {
      this.updateExisting();
    } else {
      this.renderNew();
      this.filterMemo = filterMemo;
    }
  }
  pushFilters() {
    const selected = this.selected.filter((v) => v !== "All");
    this.instance.triggerFilter(this.filter, selected);
  }
  pillInner(val, count) {
    if (this.total) {
      return `<span aria-label="${val}">${val} (${count})</span>`;
    } else {
      return `<span aria-label="${val}">${val}</span>`;
    }
  }
  renderNew() {
    this.available.forEach(([val, count]) => {
      new El("button").class("pagefind-modular-filter-pill").html(this.pillInner(val, count)).attrs({
        "aria-pressed": this.selected.includes(val),
        "type": "button"
      }).handle("click", () => {
        if (val === "All") {
          this.selected = ["All"];
        } else if (this.selected.includes(val)) {
          this.selected = this.selected.filter((v) => v !== val);
        } else if (this.selectMultiple) {
          this.selected.push(val);
        } else {
          this.selected = [val];
        }
        if (!this.selected?.length) {
          this.selected = ["All"];
        } else if (this.selected?.length > 1) {
          this.selected = this.selected.filter((v) => v !== "All");
        }
        this.update();
        this.pushFilters();
      }).addTo(this.pillContainer);
    });
  }
  updateExisting() {
    const pills = [...this.pillContainer.childNodes];
    this.available.forEach(([val, count], i) => {
      pills[i].innerHTML = this.pillInner(val, count);
      pills[i].setAttribute("aria-pressed", this.selected.includes(val));
    });
  }
  register(instance) {
    this.instance = instance;
    this.instance.on("filters", (filters) => {
      if (!this.pillContainer)
        return;
      if (this.selectMultiple) {
        filters = filters.available;
      } else {
        filters = filters.total;
      }
      let newlyAvailable = filters[this.filter];
      if (!newlyAvailable) {
        console.warn(`[Pagefind FilterPills component]: No possible values found for the ${this.filter} filter`);
        return;
      }
      this.available = Object.entries(newlyAvailable);
      if (Array.isArray(this.ordering)) {
        this.available.sort((a, b) => {
          const apos = this.ordering.indexOf(a[0]);
          const bpos = this.ordering.indexOf(b[0]);
          return (apos === -1 ? Infinity : apos) - (bpos === -1 ? Infinity : bpos);
        });
      } else {
        this.available.sort((a, b) => {
          return a[0].localeCompare(b[0]);
        });
      }
      this.available.unshift(["All", this.total]);
      this.update();
    });
    instance.on("results", (results) => {
      if (!this.pillContainer)
        return;
      this.total = results?.unfilteredResultCount || 0;
      if (this.available?.[0]?.[0] === "All") {
        this.available[0][1] = this.total;
      }
      if (this.total || this.alwaysShow) {
        this.wrapper.removeAttribute("data-pfmod-hidden");
      } else {
        this.wrapper.setAttribute("data-pfmod-hidden", "true");
      }
      this.update();
    });
  }
};

// modular-core.js
var sleep = async (ms = 50) => await new Promise((resolve) => setTimeout(resolve, ms));
var scriptBundlePath;
try {
  if (document?.currentScript && document.currentScript.tagName.toUpperCase() === "SCRIPT") {
    scriptBundlePath = new URL(document.currentScript.src).pathname.match(
      /^(.*\/)(?:pagefind-)?modular-ui.js.*$/
    )[1];
  }
} catch (e) {
  scriptBundlePath = "/pagefind/";
}
var Instance = class {
  constructor(opts = {}) {
    this.__pagefind__ = null;
    this.__initializing__ = null;
    this.__searchID__ = 0;
    this.__hooks__ = {
      search: [],
      filters: [],
      loading: [],
      results: []
    };
    this.components = [];
    this.searchTerm = "";
    this.searchFilters = {};
    this.searchResult = {};
    this.availableFilters = null;
    this.totalFilters = null;
    this.options = {
      bundlePath: opts.bundlePath ?? scriptBundlePath,
      //TODO: USE resetStyles: opts.resetStyles ?? true,
      //TODO: USE processResult: opts.processResult ?? null,
      //TODO: USE processTerm: opts.processTerm ?? null,
      mergeIndex: opts.mergeIndex ?? []
      //TODO: USE translations: opts.translations ?? [],
    };
    delete opts["bundlePath"];
    delete opts["resetStyles"];
    delete opts["processResult"];
    delete opts["processTerm"];
    delete opts["debounceTimeoutMs"];
    delete opts["mergeIndex"];
    delete opts["translations"];
    this.pagefindOptions = opts;
  }
  add(component) {
    component?.register?.(this);
    this.components.push(component);
  }
  on(event, callback) {
    if (!this.__hooks__[event]) {
      const supportedEvents = Object.keys(this.__hooks__).join(", ");
      console.error(
        `[Pagefind Composable]: Unknown event type ${event}. Supported events: [${supportedEvents}]`
      );
      return;
    }
    if (typeof callback !== "function") {
      console.error(
        `[Pagefind Composable]: Expected callback to be a function, received ${typeof callback}`
      );
      return;
    }
    this.__hooks__[event].push(callback);
  }
  triggerLoad() {
    this.__load__();
  }
  triggerSearch(term) {
    this.searchTerm = term;
    this.__dispatch__("search", term, this.searchFilters);
    this.__search__(term, this.searchFilters);
  }
  triggerSearchWithFilters(term, filters) {
    this.searchTerm = term;
    this.searchFilters = filters;
    this.__dispatch__("search", term, filters);
    this.__search__(term, filters);
  }
  triggerFilters(filters) {
    this.searchFilters = filters;
    this.__dispatch__("search", this.searchTerm, filters);
    this.__search__(this.searchTerm, filters);
  }
  triggerFilter(filter, values) {
    this.searchFilters = this.searchFilters || {};
    this.searchFilters[filter] = values;
    this.__dispatch__("search", this.searchTerm, this.searchFilters);
    this.__search__(this.searchTerm, this.searchFilters);
  }
  __dispatch__(e, ...args) {
    this.__hooks__[e]?.forEach((hook) => hook?.(...args));
  }
  async __clear__() {
    this.__dispatch__("results", { results: [], unfilteredTotalCount: 0 });
    this.availableFilters = await this.__pagefind__.filters();
    this.totalFilters = this.availableFilters;
    this.__dispatch__("filters", {
      available: this.availableFilters,
      total: this.totalFilters
    });
  }
  async __search__(term, filters) {
    this.__dispatch__("loading");
    await this.__load__();
    const thisSearch = ++this.__searchID__;
    if (!term || !term.length) {
      return this.__clear__();
    }
    const results = await this.__pagefind__.search(term, { filters });
    if (results && this.__searchID__ === thisSearch) {
      if (results.filters && Object.keys(results.filters)?.length) {
        this.availableFilters = results.filters;
        this.totalFilters = results.totalFilters;
        this.__dispatch__("filters", {
          available: this.availableFilters,
          total: this.totalFilters
        });
      }
      this.searchResult = results;
      this.__dispatch__("results", this.searchResult);
    }
  }
  async __load__() {
    if (this.__initializing__) {
      while (!this.__pagefind__) {
        await sleep(50);
      }
      return;
    }
    this.__initializing__ = true;
    if (!this.__pagefind__) {
      let imported_pagefind;
      try {
        imported_pagefind = await import(`${this.options.bundlePath}pagefind.js`);
      } catch (e) {
        console.error(e);
        console.error(
          [
            `Pagefind couldn't be loaded from ${this.options.bundlePath}pagefind.js`,
            `You can configure this by passing a bundlePath option to PagefindComposable Instance`
          ].join("\n")
        );
        if (document?.currentScript && document.currentScript.tagName.toUpperCase() === "SCRIPT") {
          console.error(
            `[DEBUG: Loaded from ${document.currentScript?.src ?? "bad script location"}]`
          );
        } else {
          console.error("no known script location");
        }
      }
      await imported_pagefind.options(this.pagefindOptions || {});
      for (const index of this.options.mergeIndex) {
        if (!index.bundlePath) {
          throw new Error("mergeIndex requires a bundlePath parameter");
        }
        const url = index.bundlePath;
        delete index["bundlePath"];
        await imported_pagefind.mergeIndex(url, index);
      }
      this.__pagefind__ = imported_pagefind;
    }
    this.availableFilters = await this.__pagefind__.filters();
    this.totalFilters = this.availableFilters;
    this.__dispatch__("filters", {
      available: this.availableFilters,
      total: this.totalFilters
    });
  }
};
export {
  FilterPills,
  Input,
  Instance,
  ResultList,
  Summary
};
